
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { Observable, firstValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';
import { ChatService } from '../../features/chat/services/chat.service';
import {
  API_URL, OPENAI_API_KEY, ANTHROPIC_API_KEY, emails, ENVIRONMENT,
  GCP_PROJECT_ID, GCP_ACCESS_TOKEN, GCP_ACCESS_TOKEN_ENDPOINT,
  VERTEX_AI_REGIONS,
  VERTEX_AI_CLAUDE_MODELS, VertexAIRegion, VertexAIClaudeModel
} from '../../config';
import OpenAI from 'openai';
import { ToastService } from './toast.service';
import { MetricsService } from './metrics.service';

export interface Message {
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
  copied?: boolean;
}
export interface PromptMemory {
  id: string;
  prompt: string;
  name: string;
  consultantName?: string;
  presentRole?: string;
  interviewingRole?: string;
  interviewingClient?: string;
  presentClient?: string;
  previousClient?: string;
  interviewType?: string;
  jobDescription?: string;
}
export interface QuestionDto {
  promptId: string;
  chatThreadId: string;
  question: string;
}

export interface SummaryResponse {
  title: string;
  summary: string;
}
export interface ChatHistory {
  s3Url: any;
  id: string;
  promptId: string;
  chatThreadId: string;
  question: string;
  answer: string;
  timestamp: string;
}

export interface aiKeyStatusDTO {
  id: string;
  error: string;
  aiService?: string;
  status: string;
  createdAT: Number;
}

export interface ApiKeys {
  openAiKey: string;
  claudeAiKey: string;
}

interface VertexAccessTokenResponse {
  accessToken?: string;
  access_token?: string;
  expiresAt?: number;
  expires_in?: number;
  projectId?: string;
}

@Injectable({
  providedIn: 'root'
})
export class OpenAiService {
  /**
   * Stream Claude answer using built context from frontend
   */

  serviceStatus: any;

  private openAiKey: string = '';
  private claudeAiKey: string = '';

  // GCP Vertex AI state
  private gcpProjectId: string = GCP_PROJECT_ID;
  private gcpAccessToken: string = GCP_ACCESS_TOKEN;
  private vertexAiRegion: VertexAIRegion = 'us-east5';
  private gcpAccessTokenExpiresAt: number | null = null;
  private gcpAccessTokenLastSetAt: number | null = this.gcpAccessToken ? Date.now() : null;
  private gcpTokenRefreshPromise: Promise<string> | null = null;
  private readonly gcpAssumedTokenLifetimeMs = 55 * 60 * 1000; // 55 minutes
  private gcpTokenInitialized: boolean = !!GCP_ACCESS_TOKEN;

  constructor(private http: HttpClient, private toast: ToastService, private metricsService: MetricsService) { }

  async streamClaudeAnswerWithContext(
    context: Array<{ role: string; content: string }>,
    botMessageIndex: number,
    chatService: any,
    onUpdate?: (partial: string) => void,
    abortSignal?: AbortSignal,
    model: string = 'claude-sonnet-4-5-20250929',
    interviewType?: string,
    skipStatusCheck: boolean = false,
    promptId?: string
  ): Promise<void> {
    if (!skipStatusCheck) {
      try {
        const statuses = this.serviceStatus;
        const svc = (s: any) => String(s?.aiService || s?.aiservice || '').toLowerCase();
        const claudeInactive = statuses.some((s: any) => svc(s) === 'claude' && String(s.status).toLowerCase() === 'inactive');
        if (claudeInactive) {
          try {
            await this.streamAskQuestionWithContext(context, botMessageIndex, chatService, onUpdate, abortSignal, 'gpt-5.2', interviewType, true, promptId);
            return;
          } catch (e) {
            const anyErr = e as any;
            if (anyErr?.name === 'AbortError' || anyErr?.code === 20 || anyErr?.type === 'aborted') {
              chatService.finalizeConversation();
              return;
            }
            console.error('Immediate fallback to OpenAI failed (Claude inactive):', e);
            chatService.updateBotMessage(botMessageIndex, "Sorry — we're unable to reach our AI right now. Please try again in a few minutes.");
            chatService.finalizeConversation();
            return;
          }
        }
      } catch (e) {
        console.warn('Could not check Claude status before request, proceeding with Claude attempt', e);
      }
    }
    let retryCount = 0;
    const maxRetries = 3;

    const recordErrorAndNotify = async (errorCode: string, errorMessage: string, statusCode?: string | number) => {
      await this.upsertAiKeyStatus('claude', errorCode, errorMessage, statusCode);
    };

    // Build prompt-caching payload: system blocks with cache_control + conversation messages
    const systemMsgs = context.filter(m => m.role === 'system');
    const convoMsgs = context.filter(m => m.role !== 'system').map(m => ({ role: m.role, content: m.content }));
    const systemBlocks = systemMsgs.map((m, i) => {
      const block: any = { type: 'text', text: m.content };
      if (i === 0) block.cache_control = { type: 'ephemeral' };
      return block;
    });

    const metricsStart = performance.now();
    let metricsTtfb = 0;
    let metricsFirstToken = 0;
    let metricsRecorded = false;

    const userQuestion = [...context].reverse().find(m => m.role === 'user')?.content || '';
    const recordMetric = (status: string, errorCode?: string) => {
      if (metricsRecorded) return;
      metricsRecorded = true;
      this.metricsService.record({
        provider: 'anthropic',
        model,
        interviewType: interviewType || '',
        promptId: promptId || '',
        question: userQuestion,
        ttfbMs: Math.round(metricsTtfb),
        firstTokenMs: Math.round(metricsFirstToken),
        totalDurationMs: Math.round(performance.now() - metricsStart),
        status,
        errorCode,
        isFallback: false,
      });
    };

    while (retryCount < maxRetries) {
      try {
        const fetchStart = performance.now();
        const response = await fetch('https://api.anthropic.com/v1/messages', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': this.claudeAiKey || ANTHROPIC_API_KEY,
            'anthropic-version': '2023-06-01',
            'anthropic-beta': 'prompt-caching-2024-07-31',
            'anthropic-dangerous-direct-browser-access': 'true',
          },
          body: JSON.stringify({
            model: model,
            max_tokens: 1024,
            ...(systemBlocks.length > 0 ? { system: systemBlocks } : {}),
            messages: convoMsgs,
            stream: true,
          }),
          signal: abortSignal,
        });
        metricsTtfb = performance.now() - fetchStart;

        if (!response.ok) {
          throw { message: `API responded with status ${response.status}`, status: response.status, code: String(response.status), headers: response.headers };
        }

        if (!response.body) {
          throw new Error('No response body returned from Claude API');
        }
        console.log('Claude streaming response started', response);
        const reader = response.body.getReader();
        let fullText = '';
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value);
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            const trimmed = line.trim();
            if (!trimmed.startsWith('data:')) continue;

            const jsonStr = trimmed.replace('data:', '').trim();
            if (jsonStr === '[DONE]') {
              recordMetric('success');
              chatService.finalizeConversation();
              return;
            }

            try {
              const parsed = JSON.parse(jsonStr);
              if (parsed.type === 'content_block_delta') {
                const delta = parsed.delta?.text;
                if (delta) {
                  if (!metricsFirstToken && delta.trim()) {
                    metricsFirstToken = performance.now() - metricsStart;
                  }
                  fullText += delta;
                  chatService.updateBotMessage(botMessageIndex, fullText);
                  if (onUpdate) onUpdate(fullText);
                }
              }
            } catch (e) {
              console.error('Error parsing response data:', e);
            }
          }
        }

        recordMetric('success');
        chatService.finalizeConversation();
        break;

      } catch (err) {
        // If the provided abortSignal was triggered by the user (clicking stop),
        // prefer to short-circuit immediately and avoid retry logic.
        if (abortSignal?.aborted) {
          recordMetric('aborted');
          chatService.finalizeConversation();
          return;
        }
        const anyErr = err as any;
        if (anyErr?.name === 'AbortError' || anyErr?.code === 20 || anyErr?.type === 'aborted') {
          recordMetric('aborted');
          chatService.finalizeConversation();
          return;
        }

        console.log('Claude stream error encountered:', err);
        retryCount++;
        console.log(`Claude retry attempt ${retryCount} of ${maxRetries}`);

        // After the first failed attempt, check status: if inactive, immediate fallback
        // if (retryCount === 1) {
        //   try {
        //     const inactiveNow = await hasInactiveStatus('claude');
        //     if (inactiveNow) {
        //       // immediate fallback to OpenAI
        //       try {
        //         await this.streamAskQuestionWithContext(context, botMessageIndex, chatService, onUpdate, abortSignal, 'gpt-5.2', interviewType);
        //       } catch (fallbackErr) {
        //         const anyErr = err as any;
        //         if (anyErr?.name === 'AbortError' || anyErr?.code === 20 || anyErr?.type === 'aborted') {
        //           chatService.finalizeConversation();
        //           return;
        //         }
        //         console.error('Fallback to OpenAI failed after detecting inactive status (Claude):', fallbackErr);
        //         chatService.updateBotMessage(botMessageIndex, 'Error: Could not reach either Claude or OpenAI service');
        //         chatService.finalizeConversation();
        //         return;
        //       }
        //       chatService.finalizeConversation();
        //       return;
        //     }
        //   } catch (e) {
        //     // ignore status-check errors
        //   }
        // }

        // If not last attempt, show retry message and wait then retry
        if (retryCount < maxRetries) {
          chatService.updateBotMessage(botMessageIndex, 'Thinking...');
          await new Promise(res => setTimeout(res, 1000));
          continue;
        }

        // Final failure: record and notify, then fallback
        const extractedError = (() => {
          if (err && typeof err === 'object') {
            const anyErr = err as any;
            const statusFromErr = anyErr?.status ?? anyErr?.statusCode ?? anyErr?.response?.status
              ?? ((anyErr?.headers && typeof anyErr.headers.get === 'function') ? (anyErr.headers.get('status') || anyErr.headers.get('x-status-code')) : anyErr?.headers ?? undefined);
            return {
              code: anyErr?.code ?? String(statusFromErr ?? 'UNKNOWN_ERROR'),
              message: anyErr?.message ?? String(anyErr) ?? 'Unknown error during Claude request',
              status: statusFromErr
            } as any;
          }
          return { code: 'UNKNOWN_ERROR', message: String(err) || 'Unknown error during Claude request' } as any;
        })();

        await recordErrorAndNotify(extractedError.code, extractedError.message, extractedError.status);
        recordMetric('error', extractedError.code);

        // Fallback to OpenAI if configured
        if (interviewType === 'finalround') {
          try {
            await this.streamAskQuestionWithContext(context, botMessageIndex, chatService, onUpdate, abortSignal, 'gpt-5.2', interviewType, true, promptId);
            return;
          } catch (fallbackError) {
            const anyErr = err as any;
            if (anyErr?.name === 'AbortError' || anyErr?.code === 20 || anyErr?.type === 'aborted') {
              chatService.finalizeConversation();
              return;
            }
            console.error('Fallback to OpenAI failed after Claude error and marking inactive:', fallbackError);
            chatService.updateBotMessage(botMessageIndex, 'Our Otter AI assistant is temporarily unavailable. Please refresh the page and try again in a few minutes.');
            chatService.finalizeConversation();
            return;
          }
        } else {
          chatService.updateBotMessage(botMessageIndex, 'Something went wrong while generating a response. Please try sending your message again.');
          chatService.finalizeConversation();
          return;
        }
      }
    }
  }

  /**
   * Upload base64 image/file to backend and get S3 URL
   */
  uploadBase64File(request: { base64Content: string, fileExtension?: string }): Observable<string> {
    // Default fileExtension to .jpg if not provided
    const body = {
      base64Content: request.base64Content,
      fileExtension: request.fileExtension || '.jpg'
    };
    return this.http.post<string>(`${API_URL}/uploadScreenshotBase64`, body);
  }

  /**
   * Upload text file to backend and get S3 URL
   */
  uploadTextFile(request: { textContent: string, fileName: string }): Observable<string> {
    return this.http.post<string>(`${API_URL}/uploadText`, request);
  }
  // Expected backend endpoints:
  // GET /api/prompts/:promptId -> { prompt: string }
  // GET /api/chat-history?promptId=... -> [{ question, answer, timestamp }]
  // POST /api/openai-stream { messages: [...] } -> streams OpenAI response
  // GET /api/ask?promptId=...&question=...&answer=... -> saves answer
  /**
   * Stream OpenAI answer using built context from frontend
   */
  // async streamAskQuestionWithContext(
  //   context: Array<{ role: string; content: string }>,
  //   botMessageIndex: number,
  //   chatService: any,
  //   onUpdate?: (partial: string) => void,
  //   abortSignal?: AbortSignal,
  //   model: string = 'gpt-4o',
  //   interviewType?: string
  // ): Promise<void> {
  //   const openai = new OpenAI({
  //     apiKey: OPENAI_API_KEY,
  //     dangerouslyAllowBrowser: true,
  //     timeout: 15000, // 15 second timeout
  //     maxRetries: 2,
  //   });

  //   let fullText = '';

  //   try {
  //     // Optimize input text construction
  //     const inputText = context.reduce((acc, m) => acc + m.content + '\n', '');

  //     const stream = await openai.responses.create(
  //       {
  //         model,
  //         input: inputText,
  //         stream: true,
  //         temperature: 0.1,
  //       },
  //       { signal: abortSignal }
  //     );

  //     for await (const event of stream) {
  //       if (event.type === 'response.output_text.delta' && typeof event.delta === 'string') {
  //         fullText += event.delta;
  //         chatService.updateBotMessage(botMessageIndex, fullText);
  //         if (onUpdate) onUpdate(fullText);
  //       }
  //     }
  //   } catch (err) {
  //     console.error('OpenAI Stream Error:', err);

  //     // Fallback logic if OpenAI fails
  //     try {
  //       if (interviewType === 'firstround') {
  //         // Try Claude Sonnet 4.5 as a fallback
  //         await this.streamClaudeAnswerWithContext(
  //           context,
  //           botMessageIndex,
  //           chatService,
  //           onUpdate,
  //           abortSignal,
  //           'claude-sonnet-4-5-20250929',
  //           interviewType
  //         );
  //         return;
  //       }

  //       if (interviewType === 'practice' || interviewType === 'screening') {
  //         // Try older Claude 3.7 as a fallback
  //         // Try Claude Sonnet 4.5 as a fallback
  //         await this.streamClaudeAnswerWithContext(
  //           context,
  //           botMessageIndex,
  //           chatService,
  //           onUpdate,
  //           abortSignal,
  //           'claude-sonnet-4-5-20250929',
  //           interviewType
  //         );
  //         return;
  //       }
  //     } catch (fallbackErr) {
  //       console.error('Fallback attempt after OpenAI failure also failed:', fallbackErr);
  //       // If fallback also fails, inform the user
  //       chatService.updateBotMessage(botMessageIndex, 'Error: Could not reach Claude or OpenAI services');
  //     }

  //     chatService.updateBotMessage(botMessageIndex, 'Error: Could not process the response');
  //   } finally {
  //     chatService.finalizeConversation();
  //   }
  // }

  async streamAskQuestionWithContext(
    context: Array<{ role: string; content: string }>,
    botMessageIndex: number,
    chatService: any,
    onUpdate?: (partial: string) => void,
    abortSignal?: AbortSignal,
    model: string = 'gpt-4o',
    interviewType?: string,
    skipStatusCheck: boolean = false,
    promptId?: string,
    skipFallback: boolean = false
  ): Promise<void> {
    if (!skipStatusCheck) {
      try {
        const statuses = this.serviceStatus;
        const svc = (s: any) => String(s?.aiService || s?.aiservice || '').toLowerCase();
        const openaiInactive = statuses.some((s: any) => svc(s) === 'openai' && String(s.status).toLowerCase() === 'inactive');
        if (openaiInactive) {
          const claudeInactive = statuses.some((s: any) => svc(s) === 'claude' && String(s.status).toLowerCase() === 'inactive');
          if (claudeInactive) {
            chatService.updateBotMessage(botMessageIndex, 'Our AI services are temporarily unavailable. Please try again in a few minutes.');
            chatService.finalizeConversation();
            return;
          }
          // route to Vertex AI Claude fallback (single attempt, no extra retries)
          try {
            console.log('[OpenAI Status] OpenAI inactive, falling back to Vertex AI Claude');
            await this.streamVertexAIClaudeAnswerWithContext(
              context, botMessageIndex, chatService, onUpdate, abortSignal,
              'anthropic-claude-sonnet-4-6', interviewType, undefined, promptId
            );
            return;
          } catch (e) {
            console.error('Immediate fallback to Vertex AI Claude failed (OpenAI inactive):', e);
            chatService.updateBotMessage(botMessageIndex, 'Our AI services are temporarily unavailable. Please try again in a few minutes.');
            chatService.finalizeConversation();
            return;
          }
        }
      } catch (e) {
        console.warn('Could not check OpenAI/Claude status before request, proceeding with OpenAI attempt', e);
      }
    }

    let retryCount = 0;
    const maxRetries = 3;
    let fullText = '';


    // const hasInactiveStatus = async (serviceName?: string): Promise<boolean> => {
    //   try {
    //     // Ensure stale inactive records are refreshed to active if past threshold
    //     await this.refreshAiKeyStatusesIfNeeded(480);
    //     const statuses: any = await this.getAiKeyStatus().pipe(take(1)).toPromise();
    //     const safeStatuses = Array.isArray(statuses) ? statuses : [];
    //     return safeStatuses.some((status) => {
    //       const isInactive = String(status?.status).toLowerCase() === 'inactive';
    //       if (!isInactive) return false;
    //       if (!serviceName) return true;
    //       const svc = String(status?.aiService || status?.aiservice || '').toLowerCase();
    //       return svc === serviceName.toLowerCase();
    //     });
    //   } catch (e) {
    //     return false;
    //   }
    // };

    const recordErrorAndNotify = async (errorCode: string, errorMessage: string, statusCode?: string | number) => {
      await this.upsertAiKeyStatus('openai', errorCode, errorMessage, statusCode);
    };

    // Build structured input array for OpenAI Responses API (preserves roles)
    const gptInput: any[] = [];
    for (const msg of context) {
      if (msg.role === 'system') {
        gptInput.push({ role: 'system', content: msg.content });
      } else if (msg.role === 'user') {
        gptInput.push({ role: 'user', content: [{ type: 'input_text', text: msg.content }] });
      } else if (msg.role === 'assistant') {
        gptInput.push({ role: 'assistant', content: [{ type: 'output_text', text: msg.content }] });
      }
    }

    const oaiMetricsStart = performance.now();
    let oaiTtfb = 0;
    let oaiFirstToken = 0;
    let oaiMetricsRecorded = false;

      const oaiUserQuestion = [...context].reverse().find(m => m.role === 'user')?.content || '';
      const recordOaiMetric = (status: string, errorCode?: string) => {
        if (oaiMetricsRecorded) return;
        oaiMetricsRecorded = true;
        this.metricsService.record({
          provider: 'openai',
          model,
          interviewType: interviewType || '',
          promptId: promptId || '',
          question: oaiUserQuestion,
          ttfbMs: Math.round(oaiTtfb),
          firstTokenMs: Math.round(oaiFirstToken),
          totalDurationMs: Math.round(performance.now() - oaiMetricsStart),
          status,
          errorCode,
          isFallback: false,
        });
      };

    while (retryCount < maxRetries) {
      try {
        const openai = new OpenAI({
          apiKey: this.openAiKey || OPENAI_API_KEY,
          dangerouslyAllowBrowser: true,
          timeout: 15000,
          maxRetries: 0,
        });

        const stream = await openai.responses.create(
          {
            model,
            input: gptInput,
            stream: true,
            temperature: 0.5,
          },
          { signal: abortSignal }
        );
        oaiTtfb = performance.now() - oaiMetricsStart;

        fullText = '';
        for await (const event of stream) {
          if (event.type === 'response.output_text.delta' && typeof event.delta === 'string') {
            if (!oaiFirstToken && event.delta.trim()) {
              oaiFirstToken = performance.now() - oaiMetricsStart;
            }
            fullText += event.delta;
            chatService.updateBotMessage(botMessageIndex, fullText);
            if (onUpdate) onUpdate(fullText);
          }
        }

        recordOaiMetric('success');
        break; // success

      } catch (err) {
        // If the caller's abortSignal has been triggered (user pressed stop),
        // short-circuit and do not run retry/fallback logic.
        if (abortSignal?.aborted) {
          recordOaiMetric('aborted');
          chatService.finalizeConversation();
          return;
        }
        // Check if this was an AbortError from the underlying SDK/DOM - if so, exit gracefully
        const anyErr = err as any;
        if (anyErr?.name === 'AbortError' || anyErr?.code === 20 || anyErr?.type === 'aborted') {
          recordOaiMetric('aborted');
          chatService.finalizeConversation();
          return;
        }

        retryCount++;

        if (retryCount < maxRetries) {
          chatService.updateBotMessage(botMessageIndex, 'Thinking...');
          await new Promise(res => setTimeout(res, 1000));
          continue;
        }

        const extractedError = (() => {
          if (err && typeof err === 'object') {
            const anyErr = err as any;
            const statusFromErr = anyErr?.status ?? anyErr?.statusCode ?? anyErr?.response?.status
              ?? ((anyErr?.headers && typeof anyErr.headers.get === 'function') ? (anyErr.headers.get('status') || anyErr.headers.get('x-status-code')) : anyErr?.headers ?? undefined);
            return {
              code: anyErr?.code ?? String(statusFromErr ?? 'UNKNOWN_ERROR'),
              message: anyErr?.message ?? String(anyErr) ?? 'Unknown error during OpenAI request',
              status: statusFromErr
            } as any;
          }
          return { code: 'UNKNOWN_ERROR', message: String(err) || 'Unknown error during OpenAI request' } as any;
        })();

        await recordErrorAndNotify(extractedError.code, extractedError.message, extractedError.status);
        recordOaiMetric('error', extractedError.code);

        // attempt fallback to Vertex AI Claude (single attempt, no extra retries)
        // Skip fallback if already in fallback mode to prevent infinite cycle
        if (!skipFallback && (interviewType === 'firstround' || interviewType === 'practice' || interviewType === 'screening' || interviewType === 'finalround')) {
          try {
            console.log(`[OpenAI Fallback] Falling back to Vertex AI Claude for interviewType=${interviewType}`);
            await this.streamVertexAIClaudeAnswerWithContext(
              context, botMessageIndex, chatService, onUpdate, abortSignal,
              'anthropic-claude-sonnet-4-6', interviewType, undefined, promptId
            );
            return;
          } catch (fallbackErr: any) {
            if (fallbackErr?.name === 'AbortError' || abortSignal?.aborted) {
              chatService.finalizeConversation();
              return;
            }
            console.error('Fallback to Vertex AI Claude failed after OpenAI error:', fallbackErr);
          }
        }

        chatService.updateBotMessage(botMessageIndex, 'Something went wrong while generating a response. Please try sending your message again.');
        chatService.finalizeConversation();
        return;
      }
    }
  }


  /**
   * Build context for OpenAI streaming: fetch prompt and chat history from backend, assemble context in frontend
   */
  async buildContext(promptId: string, question: string): Promise<Array<{ role: string; content: string }>> {
    // 1. Fetch system prompt using HttpClient
    const promptData: any = await this.http.get(`${API_URL}/prompt/${promptId}`).toPromise();
    const context: Array<{ role: string; content: string }> = [];
    const identityGuardrail = '\n\nIMPORTANT: You are Otter AI, an AI assistant built by Otter. You are NOT ChatGPT, not made by OpenAI, and not Claude. Never claim to be any other AI. If asked who you are or who made you, always say you are Otter AI.';
    if (promptData && promptData.prompt) {
      context.push({ role: 'system', content: promptData.prompt + identityGuardrail });
    }
    // 2. Fetch chat history using HttpClient
    const historyData: any[] = await this.http.get<any[]>(`${API_URL}/chat-history/${encodeURIComponent(promptId)}`).toPromise() || [];
    // 3. Sort by timestamp descending, take last 2 messages
    const sorted = (historyData || []).sort((a: any, b: any) => Number(b.timestamp) - Number(a.timestamp));
    let count = 0;
    for (const chat of sorted) {
      if (count === 2) break;
      if (chat.question) {
        context.push({ role: 'user', content: chat.question });
      }
      if (chat.answer) {
        context.push({ role: 'assistant', content: chat.answer });
      }
      count++;
    }
    // 4. Add current user question
    context.push({ role: 'user', content: question });
    return context;
  }
  async ClaudeBuildContext(promptId: string, question: string): Promise<Array<{ role: string; content: string }>> {
    // 1. Fetch system prompt using HttpClient
    const promptData: any = await this.http.get(`${API_URL}/prompt/${promptId}`).toPromise();
    const context: Array<{ role: string; content: string }> = [];
    const identityGuardrail = '\n\nIMPORTANT: You are Otter AI, an AI assistant built by Otter. You are NOT ChatGPT, not made by OpenAI, and not Claude. Never claim to be any other AI. If asked who you are or who made you, always say you are Otter AI.';
    // Claude expects the system prompt as the first 'assistant' message
    if (promptData && promptData.prompt) {
      context.push({ role: 'assistant', content: promptData.prompt + identityGuardrail });
    }
    // 2. Fetch chat history using HttpClient
    const historyData: any[] = await this.http.get<any[]>(`${API_URL}/chat-history/${encodeURIComponent(promptId)}`).toPromise() || [];
    // 3. Sort by timestamp ascending, take last 3-4 Q&A pairs
    const sorted = (historyData || []).sort((a: any, b: any) => Number(a.timestamp) - Number(b.timestamp));
    let pairs = 0;
    for (const chat of sorted.reverse()) {
      if (pairs === 4) break;
      if (chat.question) {
        context.push({ role: 'user', content: chat.question });
      }
      if (chat.answer) {
        context.push({ role: 'assistant', content: chat.answer });
      }
      pairs++;
    }
    // 4. Add current user question
    context.push({ role: 'user', content: question });
    return context;
  }


  async buildContextFromPreloadedData(promptData: any, question: string, promptId: string): Promise<Array<{ role: string; content: string }>> {
    const context: Array<{ role: string; content: string }> = [];
    const identityGuardrail = '\n\nIMPORTANT: You are Otter AI, an AI assistant built by Otter. You are NOT ChatGPT, not made by OpenAI, and not Claude. Never claim to be any other AI. If asked who you are or who made you, always say you are Otter AI.';

    // Add system prompt as the first assistant message (Claude-style)
    if (promptData && promptData.prompt) {
      context.push({ role: 'assistant', content: promptData.prompt + identityGuardrail });
    }

    // Fetch chat history for the given promptId (service is responsible for history retrieval)
    const historyData: any[] = await this.http.get<any[]>(`${API_URL}/chat-history/${encodeURIComponent(promptId)}`).toPromise() || [];

    // Sort by timestamp ascending (oldest first) using Date parsing to be robust
    const sorted = (historyData || []).sort((a: any, b: any) => (new Date(a.timestamp).getTime() || 0) - (new Date(b.timestamp).getTime() || 0));

    // Include up to the last 4 Q/A pairs, but push them in oldest->newest order
    const maxPairs = 4;
    const startIndex = Math.max(0, sorted.length - maxPairs);
    for (let i = startIndex; i < sorted.length; i++) {
      const chat = sorted[i];
      if (chat.question) {
        context.push({ role: 'user', content: chat.question });
      }
      if (chat.answer) {
        context.push({ role: 'assistant', content: chat.answer });
      }
    }

    // Add current user question
    context.push({ role: 'user', content: question });
    return context;
  }
  /**
   * Save answer to backend after streaming is complete
   */
async saveAnswer(
  promptId: string,
  question: string,
  answer: string,
  s3Url?: string
): Promise<any> {
  let url = `${API_URL}/save-response`;
  const params = new URLSearchParams({ promptId });

  if (s3Url) {
    params.append('s3Url', s3Url);
  }

  url += `?${params.toString()}`;

  const body = {
    question,
    answer
  };

  try {
    // Convert Observable to Promise so callers using `await` will actually execute and wait
    const resp = await firstValueFrom(this.http.post(url, body));
    return resp;
  } catch (err) {
    console.error('saveAnswer failed', err);
    throw err;
  }
}



  /**
   * Send both text and file URL to OpenAI /v1/responses endpoint using fetch
   */
  sendTextAndFileToOpenAI(inputText: string, fileUrl: string): Observable<any> {
    const openAiApiKey =  this.openAiKey || OPENAI_API_KEY;
    const body = {
      model: 'gpt-5.2',
      input: [
        {
          role: 'user',
          content: [
            { type: 'input_text', text: inputText },
            { type: 'input_file', file_url: fileUrl }
          ]
        }
      ]
    };

    return new Observable(observer => {
      fetch('https://api.openai.com/v1/responses', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${openAiApiKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      })
        .then(response => {
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }
          return response.json();
        })
        .then(data => {
          observer.next(data);
          observer.complete();
        })
        .catch(error => {
          observer.error(error);
        });
    });
  }

  /**
   * Send both text and image URL to OpenAI /v1/responses endpoint using fetch
   */
  sendTextAndImageToOpenAI(inputText: string, imageUrl: string): Observable<any> {
    const openAiApiKey = this.openAiKey || OPENAI_API_KEY;
    const body = {
      model: 'gpt-5.2',
      input: [
        {
          role: 'user',
          content: [
            { type: 'input_text', text: inputText },
            { type: 'input_image', image_url: imageUrl }
          ]
        }
      ]
    };

    return new Observable(observer => {
      fetch('https://api.openai.com/v1/responses', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${openAiApiKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      })
        .then(response => {
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }
          return response.json();
        })
        .then(data => {
          observer.next(data);
          observer.complete();
        })
        .catch(error => {
          observer.error(error);
        });
    });
  }

  getOpenApiData(messages: Message[]): Observable<string> {
    const payload = messages.map(msg => ({
      role: msg.sender,
      content: msg.text
    }));
    return this.http.post(`${API_URL}/generate-doc`, payload, { responseType: 'text' });
  }

  getSummary(messages: Message[]): Observable<SummaryResponse> {
    const payload = messages.map(msg => ({
      role: msg.sender,
      content: msg.text,
    }));
    return this.http.post<SummaryResponse>(`${API_URL}/summarize`, payload);
  }
  getAllPrompts(): Observable<PromptMemory[]> {
    return this.http.get<PromptMemory[]>(`${API_URL}/prompts`);
  }
  askQuestion(dto: QuestionDto): Observable<any> {
    return this.http.post(`${API_URL}/ask`, dto);
  }
  getPromptById(id: string): Observable<PromptMemory> {
    return this.http.get<PromptMemory>(`${API_URL}/prompt/${id}`);
  }
  updatePrompt(id: string, prompt: string): Observable<string> {
    return this.http.put(`${API_URL}/prompt/${id}`, { id, prompt }, { responseType: 'text' });
  }
  deletePrompt(id: string): Observable<string> {
    return this.http.delete(`${API_URL}/prompt/${id}`, { responseType: 'text' });
  }
  getAllChatHistory(chatId: string): Observable<ChatHistory[]> {
    return this.http.get<ChatHistory[]>(`${API_URL}/chat-history/${chatId}`);
  }
  getChatHistoryByThread(chatThreadId: string): Observable<ChatHistory[]> {
    return this.http.get<ChatHistory[]>(`${API_URL}/history/thread/${chatThreadId}`);
  }
  getChatHistoryByPromptId(promptId: string): Observable<ChatHistory[]> {
    return this.http.get<ChatHistory[]>(`${API_URL}/chat-history/${promptId}`);
  } 

  
  getUserChatHistoryByPromptId(promptId: string): Observable<ChatHistory[]> {
    return this.http.get<ChatHistory[]>(`${API_URL}/user/chathistory/${promptId}`);
  }
  deleteChatHistory(id: string): Observable<string> {
    return this.http.delete(`${API_URL}/history/${id}`, { responseType: 'text' });
  }
  getAiKeyStatus(): Observable<aiKeyStatusDTO[]> {
    return this.http.get<aiKeyStatusDTO[]>(`${API_URL}/aiKeyStatus/keys/all`);
  }

  createAiKeyStatus(payload: aiKeyStatusDTO): Observable<aiKeyStatusDTO> {
    return this.http.post<aiKeyStatusDTO>(`${API_URL}/aiKeyStatus/keys`, payload);
  }

  updateAiKeyStatus(id: string, payload: aiKeyStatusDTO): Observable<aiKeyStatusDTO> {
    return this.http.put<aiKeyStatusDTO>(`${API_URL}/aiKeyStatus/keys/${id}`, payload);
  } 

  getApiKeys(): Observable<ApiKeys> {
    return this.http.get<ApiKeys>(`${API_URL}/keys/ai`);
  }

  aikeyStatusEmailSend(errorCode: string, errorMessage: string, statusCode?: string | number, aiService?: string): Observable<string> {
    try {
      // Prefer explicit statusCode if provided (comes from response headers or error.status),
      // otherwise fall back to provided errorCode string.
      const codeStr = String(statusCode ?? errorCode ?? '');
      // Determine recipients: if 401 or 429 -> all configured emails; otherwise only Jagadish & Sarath
      const cfg: any = emails || {};
      const allEmails = Object.values(cfg).filter(Boolean) as string[];
      const jagadish = cfg['Jagadish'] || cfg['jagadish'] || '';
      const sarath = cfg['Sarath'] || cfg['sarath'] || '';

      let recipientsEmails: string[] = [];
      if (codeStr.includes('401') || codeStr.includes('429')) {
        recipientsEmails = allEmails;
      } else {
        recipientsEmails = [jagadish, sarath].filter(Boolean);
      }

      // Build userInfoList array matching the curl format: [{ email, name }, ...]
      const userInfoList = recipientsEmails.map(email => {
        // try to infer the configured key/name from the config map, otherwise fallback to empty name
        const nameKey = Object.keys(cfg).find(k => cfg[k] === email) || '';
        const name = nameKey || (email === jagadish ? 'Jagadish' : (email === sarath ? 'Sarath' : ''));
        return { email, name };
      });

      const body = {
        errorCode: codeStr,
        errorMessage: String(errorMessage || ''),
        userInfoList,
        aiService: aiService || ''
      };

      const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
      return this.http.post<string>(`${API_URL}/aiKeyStatus/keys/check-api-key-status`, body, { headers });
    } catch (e) {
      // Fallback: if JSON build/send fails for any reason, fall back to form-url-encoded for robustness
      try {
        const cfg: any = emails || {};
        const jagadish = cfg['Jagadish'] || cfg['jagadish'] || '';
        const sarath = cfg['Sarath'] || cfg['sarath'] || '';
        let params = new HttpParams().set('errorCode', String(statusCode ?? errorCode ?? '')).set('errorMessage', String(errorMessage || ''));
        const fallRecipients = [jagadish, sarath].filter(Boolean);
        for (const r of fallRecipients) {
          params = params.append('emailAddresses[]', r);
        }
        const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
        return this.http.post<string>(`${API_URL}/aiKeyStatus/keys/check-api-key-status`, params.toString(), { headers });
      } catch (e2) {
        // Last-resort: send as simple request params using HttpParams with string values
        const lastParams = new HttpParams()
          .set('errorCode', String(errorCode ?? ''))
          .set('errorMessage', String(errorMessage ?? ''))
          .set('aiService', String(aiService ?? ''));
        // send an empty body string to ensure response typing aligns with Observable<string>
        return this.http.post<string>(`${API_URL}/aiKeyStatus/keys/check-api-key-status`, '', { params: lastParams });
      }
    }
  }

  /**
   * Refresh stored AI key statuses: if any record is inactive for >= thresholdMinutes,
   * mark it active again using the backend.
   */
  async refreshAiKeyStatusesIfNeeded(thresholdMinutes: number = 480): Promise<any> {
    try {
      const statuses: any = await this.getAiKeyStatus().pipe(take(1)).toPromise();
      const now = Date.now();
      this.serviceStatus = statuses;
      console.log('refreshAiKeyStatusesIfNeeded: fetched statuses', statuses);
      const thresholdMs = thresholdMinutes * 60 * 1000;
      for (const s of (statuses || [])) {
        try {
          if (s && String(s.status).toLowerCase() === 'inactive') {
            const created = Number(s.createdAT) || 0;
            if (now - created >= thresholdMs) {
              // Reactivating: mark active, clear any prior error message and refresh createdAT
              const updated: aiKeyStatusDTO = { ...s, status: 'active', error: '', createdAT: Date.now() } as any;
              await this.updateAiKeyStatus(s.id, updated).pipe(take(1)).toPromise();
              const statuses: any = await this.getAiKeyStatus().pipe(take(1)).toPromise();
              this.serviceStatus = statuses;
            }
          }
        } catch (e) {
          console.warn('refreshAiKeyStatusesIfNeeded: single record update failed', e);
        }
      }

      return Array.isArray(statuses) ? statuses : [];
    } catch (e) {
      console.warn('refreshAiKeyStatusesIfNeeded failed', e);
    }
  }

  /**
   * Public helper: refresh statuses (reactivate stale inactive records) and return the latest statuses.
   * Useful for callers that want to ensure statuses are up-to-date before using them.
   */
  async refreshAndGetAiKeyStatus(thresholdMinutes: number = 480): Promise<aiKeyStatusDTO[]> {
    try {
      const status = await this.refreshAiKeyStatusesIfNeeded(thresholdMinutes);
      // const statuses: any = await this.getAiKeyStatus().pipe(take(1)).toPromise();
      // return Array.isArray(statuses) ? statuses : [];
      return Array.isArray(status) ? status : [];
    } catch (e) {
      console.warn('refreshAndGetAiKeyStatus failed', e);
      return [];
    }
  }

  /**
   * Create or update an aiKeyStatus record for a given service and send notification.
   * If a record exists for the service, update it; otherwise create a new one.
   */
  private async upsertAiKeyStatus(serviceName: string, errorCode: string, errorMessage: string, statusCode?: string | number): Promise<void> {
    try {
      const statuses: any = await this.getAiKeyStatus().pipe(take(1)).toPromise();
      this.serviceStatus = statuses
      const safeStatuses = Array.isArray(statuses) ? statuses : [];
      const existing = safeStatuses.find((s: any) => String((s.aiService || s.aiservice || '')).toLowerCase() === serviceName.toLowerCase());
      const payload: aiKeyStatusDTO = {
        id: existing?.id || '',
        error: errorMessage,
        status: 'inactive',
        createdAT: Date.now(),
        aiService: serviceName
      } as any;

      if (existing && existing.id) {
        try {
          await this.updateAiKeyStatus(existing.id, payload).pipe(take(1)).toPromise();
          const statuses: any = await this.getAiKeyStatus().pipe(take(1)).toPromise();
          this.serviceStatus = statuses
        } catch (e) {
          console.warn('upsertAiKeyStatus update failed, trying create', e);
          await this.createAiKeyStatus(payload).pipe(take(1)).toPromise();
        }
      } else {
        await this.createAiKeyStatus(payload).pipe(take(1)).toPromise();
      }

      // send notification (best-effort). Pass statusCode if available so recipient logic can use it.
      try {
        await this.aikeyStatusEmailSend(errorCode, errorMessage, statusCode, serviceName).pipe(take(1)).toPromise();
      } catch (e) {
        console.warn('upsertAiKeyStatus: email send failed', e);
      }
    } catch (e) {
      console.warn('upsertAiKeyStatus failed', e);
    }
  }

  /**
   * Extracts the output text from OpenAI /v1/responses API response
   */
  extractOpenAIText(response: any): string {
    // Handles both file and image response format
    if (response && response.output && Array.isArray(response.output) && response.output.length > 0) {
      const msg = response.output[0];
      if (msg && msg.content && Array.isArray(msg.content)) {
        for (const item of msg.content) {
          if (item.type === 'output_text' && item.text) {
            return item.text;
          }
        }
      }
    }
    // fallback: try response.content.text (for legacy or direct format)
    if (response && response.content && response.content.text) {
      return response.content.text;
    }
    return '';
  } 

  // ── Vertex AI Claude Token Management ──

  private parseGcpTokenExpiry(response: VertexAccessTokenResponse): number | null {
    // Backend returns expiresAt as epoch millis
    if (typeof response.expiresAt === 'number' && Number.isFinite(response.expiresAt)) {
      return response.expiresAt;
    }
    // Fallback: backend also returns expires_in as seconds
    if (typeof response.expires_in === 'number' && Number.isFinite(response.expires_in)) {
      return Date.now() + (response.expires_in * 1000);
    }
    return null;
  }

  private hasUsableGcpToken(): boolean {
    return !!this.gcpAccessToken;
  }

  /**
   * Fetch a fresh GCP access token from the backend.
   * Called only when: (1) no token exists, or (2) we got a 401 error.
   */
  private async fetchGcpAccessToken(): Promise<string> {
    if (this.gcpTokenRefreshPromise) {
      return this.gcpTokenRefreshPromise;
    }

    this.gcpTokenRefreshPromise = (async () => {
      try {
        let response: VertexAccessTokenResponse;

        try {
          response = await firstValueFrom(
            this.http.get<VertexAccessTokenResponse>(GCP_ACCESS_TOKEN_ENDPOINT)
          );
        } catch (err: any) {
          if (err?.status === 401 || err?.status === 403) {
            throw new Error('AUTH_REQUIRED: Please log in to use Vertex AI Claude');
          }
          throw err;
        }

        const accessToken = response?.accessToken || response?.access_token || '';
        if (!accessToken) {
          throw new Error('TOKEN_EMPTY: Backend token endpoint did not return accessToken');
        }

        this.gcpAccessToken = accessToken;
        this.gcpAccessTokenLastSetAt = Date.now();
        this.gcpAccessTokenExpiresAt = this.parseGcpTokenExpiry(response) ?? (Date.now() + this.gcpAssumedTokenLifetimeMs);
        this.gcpTokenInitialized = true;

        if (response?.projectId) {
          this.gcpProjectId = response.projectId;
        }

        return this.gcpAccessToken;
      } catch (error: any) {
        if (error?.message?.includes('AUTH_REQUIRED')) {
          this.gcpAccessToken = '';
          this.gcpTokenInitialized = false;
        }
        throw error;
      } finally {
        this.gcpTokenRefreshPromise = null;
      }
    })();

    return this.gcpTokenRefreshPromise;
  }

  /**
   * Get the current GCP token - uses cache, only fetches if no token exists.
   */
  private async getGcpAccessToken(): Promise<string> {
    if (this.gcpAccessToken) {
      return this.gcpAccessToken;
    }
    return this.fetchGcpAccessToken();
  }

  /**
   * Pre-warm the GCP token in background. Call when Vertex AI is selected.
   */
  warmGcpToken(): void {
    if (this.gcpAccessToken || this.gcpTokenRefreshPromise) {
      return;
    }
    this.fetchGcpAccessToken().catch(() => {});
  }

  /**
   * One streamed Vertex AI Claude call (streamRawPredict). `messages` may carry Anthropic content blocks
   * (text and image). Reports the reply as it grows and resolves with the complete reply. Used by coding mode
   * as the fallback when OpenAI is unavailable, mirroring the normal chat's OpenAI to Vertex AI Claude fallback.
   *
   * With `tool`, Claude is forced to call that tool, so the reply is the tool's input JSON (streamed as it is
   * written) validated against `input_schema`. This is Claude's equivalent of structured outputs.
   */
  async streamVertexClaudeMessage(
    system: string,
    messages: Array<{ role: 'user' | 'assistant'; content: unknown }>,
    options: {
      modelKey?: string;
      maxTokens?: number;
      region?: VertexAIRegion;
      tool?: { name: string; description: string; input_schema: Record<string, unknown> };
    } = {},
    onText?: (text: string) => void,
    signal?: AbortSignal,
  ): Promise<string> {
    const activeRegion = options.region || this.vertexAiRegion;
    const modelKeyStr = String(options.modelKey || 'anthropic-claude-sonnet-4-6').trim();
    let modelId = (VERTEX_AI_CLAUDE_MODELS as Record<string, string | undefined>)[modelKeyStr];
    if (!modelId && /^claude[-_]/i.test(modelKeyStr)) modelId = modelKeyStr;
    if (!modelId) throw new Error(`Unknown Vertex AI Claude model: ${modelKeyStr}`);
    if (!this.gcpProjectId) throw new Error('Vertex AI is not configured (missing GCP project).');
    if (!this.gcpAccessToken && !GCP_ACCESS_TOKEN_ENDPOINT) throw new Error('Vertex AI is not configured (missing access token).');

    const isLocalDev = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
    const path = `/v1/projects/${this.gcpProjectId}/locations/${activeRegion}/publishers/anthropic/models/${modelId}:streamRawPredict`;
    let endpoint: string;
    if (isLocalDev) {
      const proxyPath = activeRegion === 'asia-southeast1' ? '/vertex-ai-asia' : activeRegion === 'global' ? '/vertex-ai-global' : '/vertex-ai-us';
      endpoint = proxyPath + path;
    } else {
      const host = activeRegion === 'global' ? 'aiplatform.googleapis.com' : `${activeRegion}-aiplatform.googleapis.com`;
      endpoint = `https://${host}${path}`;
    }

    const payload: Record<string, unknown> = {
      anthropic_version: 'vertex-2023-10-16',
      system: [{ type: 'text', text: system, cache_control: { type: 'ephemeral' } }],
      messages,
      max_tokens: options.maxTokens ?? 16000,
      stream: true,
    };
    if (options.tool) {
      payload['tools'] = [options.tool];
      payload['tool_choice'] = { type: 'tool', name: options.tool.name };
    }
    console.log(`[Vertex AI] streamRawPredict ${modelId} in ${activeRegion} | ${messages.length} messages${options.tool ? ' | tool ' + options.tool.name : ''}`);
    const response = await this.postToVertexAi(endpoint, payload, signal);
    if (!response.ok || !response.body) {
      const detail = await response.text().catch(() => '');
      throw new Error(`Vertex AI Claude returned HTTP ${response.status}. ${detail.slice(0, 300)}`.trim());
    }

    // Tool input arrives as `input_json_delta` pieces; plain replies as `text_delta` pieces.
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let reply = '';
    let stopReason = '';
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';
      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed.startsWith('data:')) continue;
        const data = trimmed.slice(5).trim();
        if (!data || data === '[DONE]') continue;
        let event: any;
        try {
          event = JSON.parse(data);
        } catch {
          continue;
        }
        if (event?.type === 'content_block_delta') {
          const piece = options.tool ? event.delta?.partial_json : event.delta?.text;
          if (typeof piece === 'string' && piece) {
            reply += piece;
            onText?.(reply);
          }
        } else if (event?.type === 'message_delta' && event.delta?.stop_reason) {
          stopReason = event.delta.stop_reason;
        } else if (event?.type === 'error') {
          throw new Error(`Vertex AI Claude stream error: ${event.error?.message || 'unknown'}`);
        }
      }
    }
    if (stopReason === 'max_tokens') {
      throw new Error('Vertex AI Claude stopped before finishing (output limit reached).');
    }
    if (!reply) throw new Error('Vertex AI Claude returned an empty reply.');
    return reply;
  }

  private async postToVertexAi(
    endpoint: string,
    payload: any,
    abortSignal?: AbortSignal,
  ): Promise<Response> {
    let accessToken: string;

    try {
      accessToken = await this.getGcpAccessToken();
    } catch (tokenErr: any) {
      if (tokenErr?.message?.includes('AUTH_REQUIRED')) {
        throw {
          status: 401,
          message: 'Please log in to use Vertex AI Claude',
          isAuthError: true
        };
      }
      throw tokenErr;
    }

    const sendRequest = async (token: string): Promise<Response> => {
      return fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
        signal: abortSignal,
      });
    };

    let response = await sendRequest(accessToken);

    // Token expired? Silently refresh and retry once
    if (response.status === 401 || response.status === 403) {
      try {
        accessToken = await this.fetchGcpAccessToken();
        response = await sendRequest(accessToken);
      } catch (refreshErr: any) {
        if (refreshErr?.message?.includes('AUTH_REQUIRED')) {
          throw {
            status: 401,
            message: 'Session expired. Please log in again to use Vertex AI Claude',
            isAuthError: true
          };
        }
        throw refreshErr;
      }
    }

    return response;
  }

  /**
   * Separate system messages into cacheable content blocks and conversation messages.
   * Adds cache_control on the first system block for Anthropic prompt caching.
   */
  private prepareClaudeCachingPayload(context: Array<{ role: string; content: string }>): {
    systemBlocks: Array<{ type: string; text: string; cache_control?: { type: string } }>;
    conversationMessages: Array<{ role: string; content: string }>;
  } {
    const systemMessages = context.filter(msg => msg.role === 'system');
    const conversationMessages = context
      .filter(msg => msg.role !== 'system')
      .map(msg => ({ role: msg.role, content: msg.content }));
    const systemBlocks = systemMessages.map((msg, index) => {
      const block: any = { type: 'text', text: msg.content };
      if (index === 0) {
        block.cache_control = { type: 'ephemeral' };
      }
      return block;
    });
    return { systemBlocks, conversationMessages };
  }

  /**
   * Stream Vertex AI Claude (GCP) answer with context.
   * Uses GCP Vertex AI endpoint for Claude models with automatic token refresh.
   * Supported regions: asia-southeast1, us-east5, global
   * Default region: us-east5
   *
   * Features:
   * - Automatic GCP access token refresh on 401/403 (via postToVertexAi)
   * - Anthropic prompt caching (first system block cached with ephemeral TTL)
   * - TTFT (Time To First Token) and total response time tracking
   * - Fallback to OpenAI gpt-5.2 on failure for screening/firstround/finalround
   * - Comprehensive error handling with user-friendly messages
   */
  async streamVertexAIClaudeAnswerWithContext(
    context: Array<{ role: string; content: string }>,
    botMessageIndex: number,
    chatService: any,
    onUpdate?: (partial: string) => void,
    abortSignal?: AbortSignal,
    modelKey: string = 'anthropic-claude-sonnet-4-6',
    interviewType?: string,
    region?: VertexAIRegion,
    promptId?: string,
  ): Promise<void> {
    const startTime = Date.now();
    let firstTokenTime: number | null = null;
    let accumulatedText = '';
    const vertexMetricsStart = performance.now();
    let vertexTtfb = 0;
    let vertexMetricsRecorded = false;

    // Resolve region and model
    const activeRegion = region || this.vertexAiRegion;
    const modelKeyStr = String(modelKey || '').trim();
    let modelId = (VERTEX_AI_CLAUDE_MODELS as Record<string, string | undefined>)[modelKeyStr];
    // Allow passing Vertex AI model IDs directly (e.g. "claude-sonnet-4-6", "claude-opus-4-6")
    if (!modelId && /^claude[-_]/i.test(modelKeyStr)) {
      modelId = modelKeyStr;
    }

    if (!modelId) {
      console.error(`Unknown Vertex AI Claude model: ${modelKeyStr}`);
      chatService.updateBotMessage(botMessageIndex, `Error: Unknown model ${modelKeyStr}`);
      chatService.finalizeConversation();
      return;
    }

    // Validate configuration
    if (!this.gcpProjectId) {
      console.error('GCP Project ID is not configured');
      chatService.updateBotMessage(botMessageIndex, 'Vertex AI is currently unavailable. Please select a different model.');
      chatService.finalizeConversation();
      return;
    }

    if (!this.gcpAccessToken && !GCP_ACCESS_TOKEN_ENDPOINT) {
      console.error('GCP Access Token is not configured');
      chatService.updateBotMessage(botMessageIndex, 'Vertex AI is currently unavailable. Please select a different model.');
      chatService.finalizeConversation();
      return;
    }

    // Build Vertex AI endpoint URL
    // Use proxy paths for local development to bypass CORS
    const isLocalDev = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
    let endpoint: string;

    if (isLocalDev) {
      let proxyPath = '/vertex-ai-us';
      if (activeRegion === 'asia-southeast1') proxyPath = '/vertex-ai-asia';
      else if (activeRegion === 'global') proxyPath = '/vertex-ai-global';
      endpoint = `${proxyPath}/v1/projects/${this.gcpProjectId}/locations/${activeRegion}/publishers/anthropic/models/${modelId}:streamRawPredict`;
    } else {
      const host = activeRegion === 'global' ? 'aiplatform.googleapis.com' : `${activeRegion}-aiplatform.googleapis.com`;
      endpoint = `https://${host}/v1/projects/${this.gcpProjectId}/locations/${activeRegion}/publishers/anthropic/models/${modelId}:streamRawPredict`;
    }

    // Prompt caching: separate system messages into cacheable content blocks
    const filteredContext = context.filter(msg => msg.content && msg.content.trim());
    const { systemBlocks, conversationMessages } = this.prepareClaudeCachingPayload(filteredContext);

    const payload: any = {
      anthropic_version: 'vertex-2023-10-16',
      ...(systemBlocks.length > 0 ? { system: systemBlocks } : {}),
      messages: conversationMessages,
      max_tokens: 2048,
      stream: true,
    };

    console.log(`[Vertex AI] Streaming ${modelId} in ${activeRegion} | ${conversationMessages.length} messages`);

    // Helper to finalize with timing metrics
    const vertexUserQuestion = [...context].reverse().find(m => m.role === 'user')?.content || '';
    const recordVertexMetric = (status: string, errorCode?: string) => {
      if (vertexMetricsRecorded) return;
      vertexMetricsRecorded = true;
      this.metricsService.record({
        provider: 'vertex-ai-claude',
        model: modelId || modelKey,
        interviewType: interviewType || '',
        promptId: promptId || '',
        question: vertexUserQuestion,
        ttfbMs: Math.round(vertexTtfb),
        firstTokenMs: firstTokenTime ? Math.round(firstTokenTime) : 0,
        totalDurationMs: Math.round(performance.now() - vertexMetricsStart),
        status,
        errorCode,
        region: activeRegion,
        isFallback: false,
      });
    };

    const finalizeWithMetrics = (totalMs?: number) => {
      const total = totalMs ?? (Date.now() - startTime);
      console.log(`[Vertex AI] Response complete: model=${modelId}, region=${activeRegion}, TTFT=${firstTokenTime}ms, total=${total}ms`);
      if (typeof chatService.updateMessageMetrics === 'function') {
        chatService.updateMessageMetrics(botMessageIndex, {
          firstTokenMs: firstTokenTime,
          totalMs: total,
          model: modelId,
          provider: 'vertex-ai-claude',
          region: activeRegion,
          timestamp: Date.now()
        });
      }
      chatService.finalizeConversation();
    };

    try {
      const vertexFetchStart = performance.now();
      const response = await this.postToVertexAi(endpoint, payload, abortSignal);
      vertexTtfb = performance.now() - vertexFetchStart;

      if (!response.ok) {
        const errorText = await response.text();
        throw {
          message: `Vertex AI API responded with status ${response.status}: ${errorText}`,
          status: response.status,
          code: String(response.status),
        };
      }

      if (!response.body) {
        throw new Error('No response body returned from Vertex AI API');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        if (abortSignal?.aborted) {
          recordVertexMetric('aborted');
          finalizeWithMetrics();
          return;
        }

        buffer += decoder.decode(value);
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed.startsWith('data:')) continue;

          const jsonStr = trimmed.replace('data:', '').trim();
          if (jsonStr === '[DONE]') {
            recordVertexMetric('success');
            finalizeWithMetrics();
            return;
          }

          try {
            const parsed = JSON.parse(jsonStr);

            // Log prompt cache usage from message_start event
            if (parsed.type === 'message_start' && parsed.message?.usage) {
              const u = parsed.message.usage;
              console.log(`[Vertex Claude Cache] write: ${u.cache_creation_input_tokens || 0}, read: ${u.cache_read_input_tokens || 0}, uncached: ${u.input_tokens || 0}`);
            }

            if (parsed.type === 'content_block_delta') {
              const delta = parsed.delta?.text;
              if (delta) {
                accumulatedText += delta;
                chatService.updateBotMessage(botMessageIndex, accumulatedText);
                if (onUpdate) onUpdate(accumulatedText);

                // Capture TTFT on first non-whitespace content
                if (firstTokenTime === null && delta.trim()) {
                  firstTokenTime = Date.now() - startTime;
                  console.log(`[Vertex AI] TTFT: ${firstTokenTime}ms (model: ${modelId}, region: ${activeRegion})`);
                  if (typeof chatService.updateMessageMetrics === 'function') {
                    chatService.updateMessageMetrics(botMessageIndex, {
                      firstTokenMs: firstTokenTime,
                      model: modelId,
                      provider: 'vertex-ai-claude',
                      region: activeRegion,
                      timestamp: Date.now()
                    });
                  }
                }
              }
            }

            if (parsed.type === 'message_stop') {
              recordVertexMetric('success');
              finalizeWithMetrics();
              return;
            }

            if (parsed.type === 'message_delta' && parsed.usage) {
              console.log('Vertex AI Claude usage:', parsed.usage);
            }
          } catch (e) {
            console.debug('Skipping non-JSON line:', trimmed);
          }
        }
      }

      // Stream ended without explicit message_stop
      recordVertexMetric('success');
      finalizeWithMetrics();

    } catch (err: any) {
      const totalTime = Date.now() - startTime;

      if (abortSignal?.aborted || err?.name === 'AbortError') {
        recordVertexMetric('aborted');
        finalizeWithMetrics(totalTime);
        return;
      }

      console.error('Vertex AI Claude stream error:', err);
      recordVertexMetric('error', err?.code || String(err?.status || 'UNKNOWN'));

      // Log error to tracking service
      try {
        const errorPayload: aiKeyStatusDTO = {
          id: '',
          error: err?.message || String(err) || 'Unknown error',
          aiService: 'vertex-ai-claude',
          status: 'error',
          createdAT: Date.now()
        } as any;
        this.createAiKeyStatus(errorPayload).pipe(take(1)).toPromise();
      } catch (e) {
        console.error('Failed to log Vertex AI Claude error:', e);
      }

      // Handle specific errors with user-friendly messages and fallback
      const errorMsg = this.getVertexAiErrorMessage(err, activeRegion, accumulatedText);

      // Fallback to OpenAI for screening/firstround/finalround
      // Pass skipFallback=true to prevent infinite cycle (Vertex→OpenAI→Vertex...)
      if (interviewType === 'screening' || interviewType === 'firstround' || interviewType === 'finalround') {
        console.log(`[Vertex AI] Failed after ${totalTime}ms, falling back to OpenAI gpt-5.2`);
        try {
          await this.streamAskQuestionWithContext(
            context, botMessageIndex, chatService, onUpdate, abortSignal, 'gpt-5.2', interviewType, true, promptId, true
          );
          return;
        } catch (fallbackErr: any) {
          if (fallbackErr?.name === 'AbortError' || abortSignal?.aborted) {
            chatService.finalizeConversation();
            return;
          }
          console.error('Fallback to OpenAI also failed:', fallbackErr);
        }
      }

      chatService.updateBotMessage(botMessageIndex, errorMsg);
      chatService.finalizeConversation();
    }
  }

  private getVertexAiErrorMessage(err: any, activeRegion: string, accumulatedText: string): string {
    const prefix = accumulatedText ? accumulatedText + '\n\n' : '';
    if (err?.isAuthError || err?.message?.includes('AUTH_REQUIRED') || err?.message?.includes('log in')) {
      return prefix + 'Please log in to use Vertex AI Claude. Your session may have expired.';
    } else if (err?.status === 401 || err?.status === 403) {
      return prefix + 'Authentication error. Please refresh the page and try again.';
    } else if (err?.status === 429) {
      return prefix + 'Rate limit exceeded. Please wait a moment and try again.';
    } else if (err?.status === 404) {
      return prefix + `Model not found in region ${activeRegion}. Please verify quota and model availability.`;
    } else if (err?.status === 503 || err?.status === 500) {
      return prefix + 'Vertex AI service is temporarily unavailable. Please try again in a few moments.';
    } else if (err?.name === 'TypeError' && err?.message?.includes('fetch')) {
      return prefix + 'Network connection lost. Please check your internet and try again.';
    }
    return prefix + `Error: ${err?.message || 'Could not reach Vertex AI Claude service'}`;
  }

  /**
   * Configure Vertex AI settings
   */
  setVertexAIConfig(projectId: string, accessToken?: string, region?: VertexAIRegion): void {
    this.gcpProjectId = projectId;
    if (accessToken !== undefined) {
      this.gcpAccessToken = accessToken;
      this.gcpAccessTokenLastSetAt = accessToken ? Date.now() : null;
      this.gcpAccessTokenExpiresAt = null;
    }
    if (region && VERTEX_AI_REGIONS.includes(region)) {
      this.vertexAiRegion = region;
    }
    console.log(`Vertex AI config set: project=${projectId}, region=${this.vertexAiRegion}`);
  }

  setKeys(openAiKey: string, claudeAiKey: string) {
    this.openAiKey = openAiKey;
    this.claudeAiKey = claudeAiKey;
  }


//   // Overlay api 

//   getChatHistory(promptId: string): Observable<ChatHistory[]> {
//     return this.http.get<ChatHistory[]>(`${API_URL}/chat-history/${promptId}`);
//   }  

//   async saveAiAnswer(
//   promptId: string,
//   question: string,
//   answer: string,
//   s3Url?: string
// ): Promise<any> {
//   let url = `${API_URL}/save-response`;
//   const params = new URLSearchParams({ promptId });

//   if (s3Url) {
//     params.append('s3Url', s3Url);
//   }

//   url += `?${params.toString()}`;

//   const body = {
//     question,
//     answer
//   };

//   try {
//     // Convert Observable to Promise so callers using `await` will actually execute and wait
//     const resp = await firstValueFrom(this.http.post(url, body));
//     return resp;
//   } catch (err) {
//     console.error('saveAnswer failed', err);
//     throw err;
//   }
// } 
 

//  getAiKeysStatus(): Observable<aiKeyStatusDTO[]> {
//     return this.http.get<aiKeyStatusDTO[]>(`${API_URL}/aiKeyStatus/keys/all`);
//   }

//   createAiKeysStatus(payload: aiKeyStatusDTO): Observable<aiKeyStatusDTO> {
//     return this.http.post<aiKeyStatusDTO>(`${API_URL}/aiKeyStatus/keys`, payload);
//   }

//   updateAiKeysStatus(id: string, payload: aiKeyStatusDTO): Observable<aiKeyStatusDTO> {
//     return this.http.put<aiKeyStatusDTO>(`${API_URL}/aiKeyStatus/keys/${id}`, payload);
//   } 

//   getApiKey(): Observable<ApiKeys> {
//     return this.http.get<ApiKeys>(`${API_URL}/keys/ai`);
//   }

//   aikeysStatusEmailSend(errorCode: string, errorMessage: string, statusCode?: string | number, aiService?: string): Observable<string> {
//     try {
//       // Prefer explicit statusCode if provided (comes from response headers or error.status),
//       // otherwise fall back to provided errorCode string.
//       const codeStr = String(statusCode ?? errorCode ?? '');
//       // Determine recipients: if 401 or 429 -> all configured emails; otherwise only Jagadish & Sarath
//       const cfg: any = emails || {};
//       const allEmails = Object.values(cfg).filter(Boolean) as string[];
//       const jagadish = cfg['Jagadish'] || cfg['jagadish'] || '';
//       const sarath = cfg['Sarath'] || cfg['sarath'] || '';

//       let recipientsEmails: string[] = [];
//       if (codeStr.includes('401') || codeStr.includes('429')) {
//         recipientsEmails = allEmails;
//       } else {
//         recipientsEmails = [jagadish, sarath].filter(Boolean);
//       }

//       // Build userInfoList array matching the curl format: [{ email, name }, ...]
//       const userInfoList = recipientsEmails.map(email => {
//         // try to infer the configured key/name from the config map, otherwise fallback to empty name
//         const nameKey = Object.keys(cfg).find(k => cfg[k] === email) || '';
//         const name = nameKey || (email === jagadish ? 'Jagadish' : (email === sarath ? 'Sarath' : ''));
//         return { email, name };
//       });

//       const body = {
//         errorCode: codeStr,
//         errorMessage: String(errorMessage || ''),
//         userInfoList,
//         aiService: aiService || ''
//       };

//       const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
//       return this.http.post<string>(`${API_URL}/aiKeyStatus/keys/check-api-key-status`, body, { headers });
//     } catch (e) {
//       // Fallback: if JSON build/send fails for any reason, fall back to form-url-encoded for robustness
//       try {
//         const cfg: any = emails || {};
//         const jagadish = cfg['Jagadish'] || cfg['jagadish'] || '';
//         const sarath = cfg['Sarath'] || cfg['sarath'] || '';
//         let params = new HttpParams().set('errorCode', String(statusCode ?? errorCode ?? '')).set('errorMessage', String(errorMessage || ''));
//         const fallRecipients = [jagadish, sarath].filter(Boolean);
//         for (const r of fallRecipients) {
//           params = params.append('emailAddresses[]', r);
//         }
//         const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
//         return this.http.post<string>(`${API_URL}/aiKeyStatus/keys/check-api-key-status`, params.toString(), { headers });
//       } catch (e2) {
//         // Last-resort: send as simple request params using HttpParams with string values
//         const lastParams = new HttpParams()
//           .set('errorCode', String(errorCode ?? ''))
//           .set('errorMessage', String(errorMessage ?? ''))
//           .set('aiService', String(aiService ?? ''));
//         // send an empty body string to ensure response typing aligns with Observable<string>
//         return this.http.post<string>(`${API_URL}/aiKeyStatus/keys/check-api-key-status`, '', { params: lastParams });
//       }
//     }
//   }

//   /**
//    * Refresh stored AI key statuses: if any record is inactive for >= thresholdMinutes,
//    * mark it active again using the backend.
//    */
//   async refreshAiKeysStatusesIfNeeded(thresholdMinutes: number = 480): Promise<any> {
//     try {
//       const statuses: any = await this.getAiKeysStatus().pipe(take(1)).toPromise();
//       const now = Date.now();
//       this.serviceStatus = statuses;
//       console.log('refreshAiKeyStatusesIfNeeded: fetched statuses', statuses);
//       const thresholdMs = thresholdMinutes * 60 * 1000;
//       for (const s of (statuses || [])) {
//         try {
//           if (s && String(s.status).toLowerCase() === 'inactive') {
//             const created = Number(s.createdAT) || 0;
//             if (now - created >= thresholdMs) {
//               // Reactivating: mark active, clear any prior error message and refresh createdAT
//               const updated: aiKeyStatusDTO = { ...s, status: 'active', error: '', createdAT: Date.now() } as any;
//               await this.updateAiKeysStatus(s.id, updated).pipe(take(1)).toPromise();
//               const statuses: any = await this.getAiKeysStatus().pipe(take(1)).toPromise();
//               this.serviceStatus = statuses;
//             }
//           }
//         } catch (e) {
//           console.warn('refreshAiKeysStatusesIfNeeded: single record update failed', e);
//         }
//       }

//       return Array.isArray(statuses) ? statuses : [];
//     } catch (e) {
//       console.warn('refreshAiKeysStatusesIfNeeded failed', e);
//     }
//   }

//   /**
//    * Public helper: refresh statuses (reactivate stale inactive records) and return the latest statuses.
//    * Useful for callers that want to ensure statuses are up-to-date before using them.
//    */
//   async refreshAndGetAiKeysStatus(thresholdMinutes: number = 480): Promise<aiKeyStatusDTO[]> {
//     try {
//       const status = await this.refreshAiKeysStatusesIfNeeded(thresholdMinutes);
//       // const statuses: any = await this.getAiKeyStatus().pipe(take(1)).toPromise();
//       // return Array.isArray(statuses) ? statuses : [];
//       return Array.isArray(status) ? status : [];
//     } catch (e) {
//       console.warn('refreshAndGetAiKeysStatus failed', e);
//       return [];
//     }
//   }

//   /**
//    * Create or update an aiKeyStatus record for a given service and send notification.
//    * If a record exists for the service, update it; otherwise create a new one.
//    */
//   private async upsertAiKeysStatus(serviceName: string, errorCode: string, errorMessage: string, statusCode?: string | number): Promise<void> {
//     try {
//       const statuses: any = await this.getAiKeysStatus().pipe(take(1)).toPromise();
//       this.serviceStatus = statuses
//       const safeStatuses = Array.isArray(statuses) ? statuses : [];
//       const existing = safeStatuses.find((s: any) => String((s.aiService || s.aiservice || '')).toLowerCase() === serviceName.toLowerCase());
//       const payload: aiKeyStatusDTO = {
//         id: existing?.id || '',
//         error: errorMessage,
//         status: 'inactive',
//         createdAT: Date.now(),
//         aiService: serviceName
//       } as any;

//       if (existing && existing.id) {
//         try {
//           await this.updateAiKeysStatus(existing.id, payload).pipe(take(1)).toPromise();
//           const statuses: any = await this.getAiKeysStatus().pipe(take(1)).toPromise();
//           this.serviceStatus = statuses
//         } catch (e) {
//           console.warn('upsertAiKeysStatus update failed, trying create', e);
//           await this.createAiKeysStatus(payload).pipe(take(1)).toPromise();
//         }
//       } else {
//         await this.createAiKeysStatus(payload).pipe(take(1)).toPromise();
//       }

//       // send notification (best-effort). Pass statusCode if available so recipient logic can use it.
//       try {
//         await this.aikeysStatusEmailSend(errorCode, errorMessage, statusCode, serviceName).pipe(take(1)).toPromise();
//       } catch (e) {
//         console.warn('upsertAiKeysStatus: email send failed', e);
//       }
//     } catch (e) {
//       console.warn('upsertAiKeysStatus failed', e);
//     }
//   }





}
